// File: src/components/TestCard.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { msToTime } from "../utils";
import { deleteTestById } from "../api";

export default function TestCard({ test, user, setTests, onEdit }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const myResult =
    user && test.results && test.results[user.uid]
      ? test.results[user.uid]
      : null;

  // compute rank
  const resultsArr = Object.entries(test.results || {}).map(([uid, r]) => ({
    uid,
    ...r,
  }));
  resultsArr.sort((a, b) => {
    const am = a.marks || 0;
    const bm = b.marks || 0;
    if (bm !== am) return bm - am;
    const at = a.timeTakenMs != null ? a.timeTakenMs : Number.MAX_SAFE_INTEGER;
    const bt = b.timeTakenMs != null ? b.timeTakenMs : Number.MAX_SAFE_INTEGER;
    return at - bt;
  });

  const rank =
    myResult && user
      ? resultsArr.findIndex((x) => x.uid === user.uid) + 1
      : null;

  const highest = resultsArr && user ? resultsArr[0]?.marks : null;

  const handleDelete = async (testId) => {
    if (!window.confirm("Are you sure you want to delete this test?")) return;
    try {
      await deleteTestById(testId);
      setTests((prev) => prev.filter((t) => t.id !== testId));
    } catch (err) {
      console.error("Failed to delete test:", err);
      alert("Failed to delete test. Try again.");
    }
  };

  // auto-close dropdown when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="p-4 border rounded shadow-sm bg-white relative">
      <div className="flex gap-2 justify-between items-start">
        <div>
          <h2 className="text-lg font-semibold">
            {test.title || "Untitled Test"}
          </h2>
          <div className="text-sm text-gray-600">
            Questions: {test.questions?.length || 0} • Time:{" "}
            {test.timeLimitMins} mins
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Owner: {test.ownerEmail ? test.ownerEmail : test.ownerUid}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Created:{" "}
            {test.createdAt
              ? new Date(
                  test.createdAt.seconds
                    ? test.createdAt.seconds * 1000 // Firestore Timestamp
                    : test.createdAt
                ).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "N/A"}
          </div>

          <div className="text-sm text-gray-700 mt-2">
            Time taken:{" "}
            {myResult?.timeTakenMs != null ? (
              msToTime(myResult.timeTakenMs)
            ) : (
              <span className="text-xs">N/A</span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 text-right justify-around mr-4">
          {myResult ? (
            <div className="text-sm font-medium text-amber-700">
              Score: {myResult.marks} / {test.questions?.length * 4 || 0}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Not attempted</div>
          )}
          {myResult && rank != null && (
            <div className="text-sm text-lime-600">
              Rank: {rank} / {resultsArr.length}
            </div>
          )}
          {resultsArr && myResult && (
            <div className="text-sm text-purple-600">Highest: {highest}</div>
          )}
          <button
            className="px-3 py-1 rounded bg-indigo-500 text-white text-sm w-full"
            onClick={() => navigate(`/${test.id}`)}
          >
            Open
          </button>
        </div>
      </div>

      {/* 3-dots menu (owner only) */}
      {user && test.ownerUid === user.uid && (
        <div className="absolute top-2 right-2" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded font-extrabold"
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-32 bg-white border rounded shadow-md z-10">
              <button
                className="block w-full text-left px-3 py-2 text-sm  hover:bg-blue-50"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit && onEdit(test);
                }}
              >
                Edit Test Details
              </button>
              <button
                onClick={() => handleDelete(test.id)}
                className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
